import { auth } from '../config/firebase';
import { RateLimitCheckResult, ResourceType, RateLimitStatuses } from '../types';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

/**
 * Limits at or above this value are presented as "Unlimited" rather than as a number.
 * -1 is honoured too. Mirrors RateLimitService.UNLIMITED_THRESHOLD in shop-svc and
 * RateLimitInfo.isUnlimited on iOS.
 *
 * CSAI-533: previously this threshold was applied in some places and `=== -1` in others, so a
 * 999999 limit rendered as "Unlimited" on the subscription screen and as the literal "999999" in
 * the rate-limit modal and banner. Never visible in practice, because no shop approached 999999
 * slides - but maxActiveClientMembers now uses the same sentinel, so the two conventions are
 * collapsed into isUnlimitedValue below and every call site routes through it.
 */
export const UNLIMITED_THRESHOLD = 999999;

/** Whether a raw limit value means "unlimited". */
export const isUnlimitedValue = (maxAllowed: number): boolean =>
  maxAllowed === -1 || maxAllowed >= UNLIMITED_THRESHOLD;

/**
 * Whether a resource's allowance resets each billing period, or is a standing limit.
 *
 * Only the AI features are metered monthly. Slides, members, client members, locations and places
 * are standing caps that never reset, so telling a user their team member limit is "for this month"
 * is wrong and implies it will clear on its own.
 *
 * Typed as Record<ResourceType, ...> so adding a ResourceType fails the build until it is
 * classified, rather than silently defaulting to the wrong wording.
 */
export const RESOURCE_GROUPS: Record<ResourceType, 'standing' | 'monthly'> = {
  SLIDES: 'standing',
  ACTIVE_MEMBERS: 'standing',
  ACTIVE_CLIENT_MEMBERS: 'standing',
  SHOP_LOCATIONS: 'standing',
  SHOP_PLACES: 'standing',
  MARKETPLACE_EVALS: 'monthly',
  QUICK_ANALYZES: 'monthly',
};

/** True when the resource's allowance resets each billing period. */
export const isMonthlyResource = (resourceType: ResourceType): boolean =>
  RESOURCE_GROUPS[resourceType] === 'monthly';

/**
 * Usage-meter escalation thresholds, as a percentage of the limit.
 *
 * Matches `SubscriptionView.usageColor` on iOS so the same shop does not read as calm on one
 * surface and alarming on the other.
 */
export const ESCALATE_WARNING_PCT = 75;
export const ESCALATE_ERROR_PCT = 90;

/**
 * Whether a meter's colour escalates toward red as it fills.
 *
 * Member meters do NOT escalate: they stay gold at any usage, matching
 * `SubscriptionView.resourceLimitMeters` on iOS, which opts them out via `escalates: false`.
 *
 * Being at your member cap is a plan fact to act on deliberately, not an error state. Painting a
 * correct, expected 2/2 in red reads as a fault the user has to fix, and a shop deliberately using
 * every seat it pays for is not in a bad state. Consumable resources are different: approaching the
 * slide or location cap genuinely is actionable, so those still escalate.
 *
 * Typed as Record<ResourceType, ...> so a new resource type must be classified rather than
 * defaulting silently.
 */
export const RESOURCE_ESCALATES: Record<ResourceType, boolean> = {
  SLIDES: true,
  ACTIVE_MEMBERS: false,
  ACTIVE_CLIENT_MEMBERS: false,
  SHOP_LOCATIONS: true,
  SHOP_PLACES: true,
  MARKETPLACE_EVALS: true,
  QUICK_ANALYZES: true,
};

/** Resource types in a group, in declaration order. */
export const resourceTypesIn = (group: 'standing' | 'monthly'): ResourceType[] =>
  (Object.keys(RESOURCE_GROUPS) as ResourceType[]).filter((rt) => RESOURCE_GROUPS[rt] === group);

// Map ResourceType to URL path segments
const RESOURCE_URL_PATHS: Record<ResourceType, string> = {
  SLIDES: 'slides',
  ACTIVE_MEMBERS: 'members',
  ACTIVE_CLIENT_MEMBERS: 'client-members',
  SHOP_LOCATIONS: 'locations',
  SHOP_PLACES: 'places',
  MARKETPLACE_EVALS: 'mpe',
  QUICK_ANALYZES: 'qa',
};

// Human-readable labels for resource types
const RESOURCE_LABELS: Record<ResourceType, string> = {
  SLIDES: 'Slides',
  ACTIVE_MEMBERS: 'Team Members',
  ACTIVE_CLIENT_MEMBERS: 'Client Members',
  SHOP_LOCATIONS: 'Locations',
  SHOP_PLACES: 'Places',
  MARKETPLACE_EVALS: 'Marketplace Evals',
  QUICK_ANALYZES: 'Quick Analyzes',
};

class RateLimitService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();
    const url = `${SHOP_API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all rate limit statuses for a shop.
   * Returns usage information for all resource types.
   */
  async getAllRateLimits(shopId: string): Promise<RateLimitStatuses> {
    return this.request<RateLimitStatuses>(`/api/v1/shops/${shopId}/rate-limits`);
  }

  /**
   * Get rate limit status for a specific resource type.
   */
  async getRateLimit(shopId: string, resourceType: ResourceType): Promise<RateLimitCheckResult> {
    const urlPath = RESOURCE_URL_PATHS[resourceType];
    return this.request<RateLimitCheckResult>(`/api/v1/shops/${shopId}/rate-limits/${urlPath}`);
  }

  /**
   * Get rate limit for slides.
   */
  async getSlideRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'SLIDES');
  }

  /**
   * Get rate limit for team members.
   */
  async getMemberRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'ACTIVE_MEMBERS');
  }

  /**
   * Get rate limit for shop locations.
   */
  async getLocationRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'SHOP_LOCATIONS');
  }

  /**
   * Get rate limit for places.
   */
  async getPlaceRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'SHOP_PLACES');
  }

  /**
   * Get rate limit for Marketplace Evals.
   */
  async getMpeRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'MARKETPLACE_EVALS');
  }

  /**
   * Get rate limit for Quick Analyzes.
   */
  async getQaRateLimit(shopId: string): Promise<RateLimitCheckResult> {
    return this.getRateLimit(shopId, 'QUICK_ANALYZES');
  }

  // Utility methods

  /**
   * Get human-readable label for a resource type.
   */
  getResourceLabel(resourceType: ResourceType): string {
    return RESOURCE_LABELS[resourceType] || resourceType;
  }

  /**
   * Get usage percentage (0-100).
   */
  getUsagePercentage(result: RateLimitCheckResult): number {
    if (isUnlimitedValue(result.maxAllowed)) return 0; // Unlimited
    if (result.maxAllowed === 0) return 100;
    return Math.min(100, Math.round((result.currentUsage / result.maxAllowed) * 100));
  }

  /**
   * Check if usage is approaching limit (>80%).
   */
  isApproachingLimit(result: RateLimitCheckResult): boolean {
    if (isUnlimitedValue(result.maxAllowed)) return false; // Unlimited
    // A non-escalating meter is never "approaching" anything: being at your member cap is a plan
    // fact, not a condition to warn about. Without this, a nudge could fire against a calm gold bar.
    if (!RESOURCE_ESCALATES[result.resourceType]) return false;
    return this.getUsagePercentage(result) >= ESCALATE_WARNING_PCT;
  }

  /**
   * Check if usage is at or over limit.
   */
  isAtLimit(result: RateLimitCheckResult): boolean {
    return !result.allowed;
  }

  /**
   * Check if resource has unlimited usage.
   */
  isUnlimited(result: RateLimitCheckResult): boolean {
    return isUnlimitedValue(result.maxAllowed);
  }

  /**
   * Get usage display string (e.g., "45 / 100" or "45 / Unlimited").
   */
  getUsageDisplay(result: RateLimitCheckResult): string {
    if (isUnlimitedValue(result.maxAllowed)) {
      return `${result.currentUsage} / Unlimited`;
    }
    return `${result.currentUsage} / ${result.maxAllowed}`;
  }

  /**
   * Get color for usage indicator based on percentage.
   */
  getUsageColor(result: RateLimitCheckResult): 'gold' | 'warning' | 'error' {
    if (isUnlimitedValue(result.maxAllowed)) return 'gold'; // Unlimited
    // Non-escalating meters stay gold at every level, including 100%. See RESOURCE_ESCALATES.
    if (!RESOURCE_ESCALATES[result.resourceType]) return 'gold';
    const percentage = this.getUsagePercentage(result);
    if (percentage >= ESCALATE_ERROR_PCT) return 'error';
    if (percentage >= ESCALATE_WARNING_PCT) return 'warning';
    return 'gold';
  }

  /**
   * Parse HTTP 429 error response to extract rate limit details.
   */
  parseRateLimitError(error: Error): RateLimitCheckResult | null {
    try {
      // Try to extract JSON from error message
      const match = error.message.match(/HTTP 429: (.+)/);
      if (match) {
        const errorBody = JSON.parse(match[1]);
        if (errorBody.error === 'rate_limit_exceeded') {
          return {
            allowed: false,
            resourceType: errorBody.resourceType,
            currentUsage: errorBody.currentUsage,
            maxAllowed: errorBody.maxAllowed,
            remaining: 0,
            tierType: errorBody.tierType,
            message: errorBody.message,
          };
        }
      }
    } catch {
      // Failed to parse, return null
    }
    return null;
  }

  /**
   * Check if an error is a rate limit error (HTTP 429).
   */
  isRateLimitError(error: Error): boolean {
    return error.message.includes('HTTP 429');
  }
}

export const rateLimitService = new RateLimitService();
